import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KLineChartPro } from '@klinecharts/pro';
import type { Period, Datafeed, SymbolInfo } from '@klinecharts/pro';
import httpClient from '@/api/httpClient';
import { useInstrument } from '@/api/instruments/use-instruments';
import { useColorMode } from '@/theme/ColorModeSelect.tsx';
import { getInstrumentQuotePrecision } from '@/utils/formatters';
import echo from '@/lib/echo';
import '@klinecharts/pro/dist/klinecharts-pro.css';

interface KLineProChartProps {
    instrumentId: string;
    symbol: string;
}

interface KLineData {
    timestamp: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

const buildSymbolInfo = (symbol: string, pricePrecision: number): SymbolInfo => ({
    exchange: 'Forex',
    market: 'forex',
    name: symbol,
    shortName: symbol,
    ticker: symbol,
    priceCurrency: 'USD',
    type: 'forex',
    pricePrecision,
    volumePrecision: 0,
});

const DEFAULT_PERIOD: Period = { multiplier: 1, timespan: 'day', text: '1D' };

const KLineProChart: React.FC<KLineProChartProps> = ({ instrumentId, symbol }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<any>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);

    // Refs for the real-time subscribe callback provided by the chart library
    const subscribeCallbackRef = useRef<((data: KLineData) => void) | null>(null);
    const latestBarRef = useRef<KLineData | null>(null);
    const { mode: themeColorMode } = useColorMode();
    const { data: instrument } = useInstrument(instrumentId);

    // Tracks the period used to create the current chart instance.
    // When the user picks a new period from the chart UI, the library's internal
    // Solid.js reactive effect can permanently lose its subscriptions if a period
    // click arrives while data is still loading (the effect returns early without
    // reading any signals, dropping all tracked dependencies). We work around this
    // by intercepting period-button clicks ourselves: when we detect the library's
    // internal period has diverged from activePeriod, we update this state which
    // re-runs the useEffect, destroys the old chart, and creates a fresh instance
    // whose reactive effect always starts with a clean subscription.
    const [activePeriod, setActivePeriod] = useState<Period>(DEFAULT_PERIOD);

    const pricePrecision = useMemo(
        () => getInstrumentQuotePrecision(instrument, symbol),
        [instrument, symbol]
    );

    // Detect mobile view and always use dark theme in mobile
    const [isMobile, setIsMobile] = React.useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth < 1024;
        }
        return false;
    });

    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fetch chart data from backend
    const fetchChartData = async (
        symbol: string,
        resolution: string,
        from: number,
        to: number
    ): Promise<KLineData[]> => {
        try {
            const params = {
                symbol: symbol,
                resolution: resolution,
                from: from,
                to: to,
            };

            console.log('Fetching chart data:', { params });

            const response = await httpClient.get('/trader/chart/history', { params });

            const data = response.data;

            console.log('Chart data response:', {
                symbol,
                status: data.s,
                dataLength: data.t?.length || 0,
                hasData: data.s === 'ok' && data.t && data.t.length > 0,
                error: data.errmsg,
            });

            if (data.s === 'ok' && data.t && data.t.length > 0) {
                const klineData: KLineData[] = [];

                for (let i = 0; i < data.t.length; i++) {
                    klineData.push({
                        timestamp: data.t[i] * 1000,
                        open: data.o[i],
                        high: data.h[i],
                        low: data.l[i],
                        close: data.c[i],
                        volume: data.v[i] || 0,
                    });
                }

                console.log(`Successfully processed ${klineData.length} candles for ${symbol}`);
                return klineData;
            }

            if (data.s === 'error') {
                console.error('Chart API error:', data.errmsg || 'Unknown error', {
                    symbol,
                    resolution,
                    from,
                    to,
                });
            } else if (data.s === 'no_data') {
                console.warn('No chart data available:', { symbol, resolution, from, to });
            } else {
                console.warn('Unexpected chart response:', data, { symbol, resolution, from, to });
            }

            return [];
        } catch (err) {
            console.error('Failed to fetch chart data:', err, { symbol, resolution, from, to });
            throw err;
        }
    };

    // Custom datafeed for KLineChart Pro
    const createCustomDatafeed = (): Datafeed => {
        return {
            searchSymbols: async (_search: string) => {
                // Return empty for now - symbol search is handled by your UI
                return [];
            },
            getHistoryKLineData: async (
                symbolInfo: SymbolInfo,
                period: Period,
                from: number,
                to: number
            ) => {
                try {
                    setError(null);

                    // Map period to resolution
                    let resolution = '1D';
                    if (period.timespan === 'minute') {
                        if (period.multiplier === 1) resolution = '1';
                        else if (period.multiplier === 5) resolution = '5';
                        else if (period.multiplier === 15) resolution = '15';
                        else if (period.multiplier === 30) resolution = '30';
                        else if (period.multiplier === 60) resolution = '60';
                        else if (period.multiplier === 240) resolution = '240';
                    } else if (period.timespan === 'day') {
                        resolution = '1D';
                    } else if (period.timespan === 'week') {
                        resolution = '1W';
                    } else if (period.timespan === 'month') {
                        resolution = '1M';
                    }

                    const data = await fetchChartData(
                        symbolInfo.ticker,
                        resolution,
                        Math.floor(from / 1000),
                        Math.floor(to / 1000)
                    );

                    // Ensure we return data even if empty to prevent chart from clearing
                    return data.length > 0 ? data : [];
                } catch (err: any) {
                    console.error('Error loading chart data:', err);
                    setError(err.message || 'Failed to load chart data');
                    // Return empty array instead of throwing to prevent chart from breaking
                    return [];
                }
            },
            subscribe: async (
                symbolInfo: SymbolInfo,
                period: Period,
                callback: (data: KLineData) => void
            ) => {
                subscribeCallbackRef.current = callback;

                // Fetch the latest bar so we have OHLCV to update on ticks
                try {
                    let resolution = '1D';
                    if (period.timespan === 'minute') {
                        if (period.multiplier === 1) resolution = '1';
                        else if (period.multiplier === 5) resolution = '5';
                        else if (period.multiplier === 15) resolution = '15';
                        else if (period.multiplier === 30) resolution = '30';
                        else if (period.multiplier === 60) resolution = '60';
                        else if (period.multiplier === 240) resolution = '240';
                    } else if (period.timespan === 'week') {
                        resolution = '1W';
                    } else if (period.timespan === 'month') {
                        resolution = '1M';
                    }
                    const now = Math.floor(Date.now() / 1000);
                    // Fetch last 3 bars to reliably get the current one
                    const periodSeconds: Record<string, number> = {
                        '1': 60,
                        '5': 300,
                        '15': 900,
                        '30': 1800,
                        '60': 3600,
                        '240': 14400,
                        '1D': 86400,
                        '1W': 604800,
                        '1M': 2592000,
                    };
                    const from = now - (periodSeconds[resolution] ?? 86400) * 3;
                    const bars = await fetchChartData(symbolInfo.ticker, resolution, from, now);
                    if (bars.length > 0) {
                        latestBarRef.current = bars[bars.length - 1];
                    }
                } catch (e) {
                    console.warn('subscribe: failed to fetch initial bar', e);
                }
            },
            unsubscribe: (_symbolInfo: SymbolInfo, _period: Period) => {
                subscribeCallbackRef.current = null;
                latestBarRef.current = null;
            },
        };
    };

    useEffect(() => {
        if (!containerRef.current || !symbol) return;

        const container = containerRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        // Wait for container to have valid dimensions
        if (width <= 0 || height <= 0) {
            // Use ResizeObserver to wait for container to get dimensions
            const observer = new ResizeObserver(entries => {
                for (const entry of entries) {
                    if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
                        observer.disconnect();
                        // Trigger re-render by updating a state or calling the effect again
                        // For now, we'll just proceed with initialization
                        break;
                    }
                }
            });
            observer.observe(container);

            // Fallback timeout
            setTimeout(() => {
                observer.disconnect();
            }, 1000);

            return () => {
                observer.disconnect();
            };
        }

        // Always use dark theme in mobile, otherwise use themeColorMode
        const isDark = isMobile || themeColorMode === 'dark';

        // Declare timeout variables for resize debouncing
        let resizeTimeout: NodeJS.Timeout | null = null;
        let windowResizeTimeout: NodeJS.Timeout | null = null;

        // Create KLineChart Pro instance
        const chart = new KLineChartPro({
            container: containerRef.current,
            theme: isDark ? 'dark' : 'light',
            symbol: buildSymbolInfo(symbol, pricePrecision),
            period: activePeriod,
            periods: [
                { multiplier: 1, timespan: 'minute', text: '1m' },
                { multiplier: 5, timespan: 'minute', text: '5m' },
                { multiplier: 15, timespan: 'minute', text: '15m' },
                { multiplier: 30, timespan: 'minute', text: '30m' },
                { multiplier: 60, timespan: 'minute', text: '1H' },
                { multiplier: 240, timespan: 'minute', text: '4H' },
                { multiplier: 1, timespan: 'day', text: '1D' },
                { multiplier: 1, timespan: 'week', text: '1W' },
                { multiplier: 1, timespan: 'month', text: '1M' },
            ] as Period[],
            locale: 'en-US',
            drawingBarVisible: true,
            timezone: 'UTC',
            datafeed: createCustomDatafeed(),
            // Configure to show minimal UI on load
            styles: {
                candle: {
                    type: 'candle_solid',
                },
            },
            mainIndicators: [], // No indicators on main chart
            subIndicators: [], // No sub-indicators (like volume)
            leftSidebarVisible: false, // Hide left panel by default
        } as any);

        chartInstanceRef.current = chart;

        // Intercept period-button clicks via event delegation so we can detect
        // when the user switches timeframes and force a fresh data load.
        // We capture in the bubble phase with a short debounce so the library
        // has already updated its internal period signal before we read it.
        let periodClickTimer: ReturnType<typeof setTimeout> | null = null;
        const handlePeriodClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            const btn = target.closest('[class*="period"]') as HTMLElement | null;
            if (!btn || !container.contains(btn)) return;
            if (periodClickTimer) clearTimeout(periodClickTimer);
            periodClickTimer = setTimeout(() => {
                const newPeriod = chartInstanceRef.current?.getPeriod?.() as Period | undefined;
                if (newPeriod && newPeriod.text !== activePeriod.text) {
                    setActivePeriod(newPeriod);
                }
            }, 50);
        };
        container.addEventListener('click', handlePeriodClick);

        // Configure chart defaults after initialization using API methods
        // Only configure UI elements, avoid clearing overlays/indicators as they might clear data
        const configureChartDefaults = () => {
            try {
                // Wait for chart to fully initialize and data to load
                setTimeout(() => {
                    if (chartInstanceRef.current) {
                        const chart = chartInstanceRef.current as any;

                        // Close left panel using API
                        if (typeof chart.setPaneOptions === 'function') {
                            // Try to minimize/close the left panel
                            chart.setPaneOptions({ id: 'left', state: 'minimize' });
                        } else if (typeof chart.setLeftPanelVisible === 'function') {
                            chart.setLeftPanelVisible(false);
                        } else if (typeof chart.applyOptions === 'function') {
                            chart.applyOptions({ leftPanelVisible: false });
                        }

                        // Clear overlays and indicators, but ensure main chart data persists
                        // First try to clear all overlays/indicators
                        if (typeof chart.clearOverlays === 'function') {
                            try {
                                chart.clearOverlays();
                            } catch (e) {
                                console.warn('Could not clear overlays:', e);
                            }
                        }
                        if (typeof chart.clearIndicators === 'function') {
                            try {
                                chart.clearIndicators();
                            } catch (e) {
                                console.warn('Could not clear indicators:', e);
                            }
                        }

                        // Also try to remove specific overlays/indicators by name as fallback
                        if (typeof chart.removeOverlay === 'function') {
                            [
                                'MA',
                                'MA5',
                                'MA10',
                                'MA20',
                                'MA30',
                                'MA50',
                                'MA100',
                                'MA200',
                                'Moving Average',
                                'VOL',
                                'Volume',
                            ].forEach(name => {
                                try {
                                    chart.removeOverlay(name);
                                } catch (e) {
                                    // Ignore if overlay doesn't exist
                                }
                            });
                        }
                        if (typeof chart.removeIndicator === 'function') {
                            [
                                'MA',
                                'MA5',
                                'MA10',
                                'MA20',
                                'MA30',
                                'MA50',
                                'MA100',
                                'MA200',
                                'Moving Average',
                                'VOL',
                                'Volume',
                            ].forEach(name => {
                                try {
                                    chart.removeIndicator(name);
                                } catch (e) {
                                    // Ignore if indicator doesn't exist
                                }
                            });
                        }

                        // Force chart to refresh/reload data after clearing to ensure data persists
                        // This ensures the main candlestick data is maintained
                        setTimeout(() => {
                            if (chartInstanceRef.current) {
                                const chart = chartInstanceRef.current as any;
                                if (typeof chart.refresh === 'function') {
                                    chart.refresh();
                                } else if (typeof chart.update === 'function') {
                                    chart.update();
                                } else if (typeof chart.reload === 'function') {
                                    chart.reload();
                                }
                            }
                        }, 100); // Small delay to ensure clearing is complete before refresh
                    }
                }, 1500); // Wait longer to ensure data is fully loaded before any configuration
            } catch (error) {
                console.warn('Could not configure chart defaults:', error);
            }
        };

        configureChartDefaults();

        // Hide watermark/logo if present
        const hideWatermark = () => {
            if (containerRef.current) {
                // Use MutationObserver to catch dynamically added watermark elements
                const observer = new MutationObserver(() => {
                    const container = containerRef.current;
                    if (container) {
                        // Hide watermark/logo elements by class, id, or data attributes
                        const selectors = [
                            '[class*="watermark"]',
                            '[class*="logo"]',
                            '[class*="branding"]',
                            '[id*="watermark"]',
                            '[id*="logo"]',
                            '[data-watermark]',
                            '[data-logo]',
                        ];

                        selectors.forEach(selector => {
                            const elements = container.querySelectorAll(selector);
                            elements.forEach(el => {
                                (el as HTMLElement).style.display = 'none';
                                (el as HTMLElement).style.visibility = 'hidden';
                                (el as HTMLElement).style.opacity = '0';
                            });
                        });

                        // Also check for any img/svg elements that might be watermarks
                        const mediaElements = container.querySelectorAll('img, svg');
                        mediaElements.forEach(el => {
                            const src = el.getAttribute('src') || '';
                            const alt = el.getAttribute('alt') || '';
                            const title = el.getAttribute('title') || '';
                            const svgEl = el as SVGElement;
                            const htmlEl = el as HTMLElement;
                            const className = (
                                svgEl.className &&
                                typeof svgEl.className === 'object' &&
                                'baseVal' in svgEl.className
                                    ? svgEl.className.baseVal
                                    : htmlEl.className?.toString() || ''
                            ).toLowerCase();

                            if (
                                src.includes('watermark') ||
                                src.includes('logo') ||
                                alt.toLowerCase().includes('watermark') ||
                                alt.toLowerCase().includes('logo') ||
                                title.toLowerCase().includes('watermark') ||
                                title.toLowerCase().includes('logo') ||
                                className.toLowerCase().includes('watermark') ||
                                className.toLowerCase().includes('logo')
                            ) {
                                (el as HTMLElement).style.display = 'none';
                                (el as HTMLElement).style.visibility = 'hidden';
                                (el as HTMLElement).style.opacity = '0';
                            }
                        });
                    }
                });

                // Start observing
                observer.observe(containerRef.current, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                });

                // Initial hide attempt
                setTimeout(() => {
                    observer.disconnect();
                    // Run one more time after chart is fully loaded
                    setTimeout(() => {
                        const container = containerRef.current;
                        if (container) {
                            const allElements = container.querySelectorAll('*');
                            allElements.forEach(el => {
                                const className = el.className?.toString() || '';
                                const id = el.id || '';
                                if (
                                    className.toLowerCase().includes('watermark') ||
                                    className.toLowerCase().includes('logo') ||
                                    id.toLowerCase().includes('watermark') ||
                                    id.toLowerCase().includes('logo')
                                ) {
                                    (el as HTMLElement).style.display = 'none';
                                    (el as HTMLElement).style.visibility = 'hidden';
                                    (el as HTMLElement).style.opacity = '0';
                                }
                            });
                        }
                    }, 1000);
                }, 2000);
            }
        };

        hideWatermark();

        // Function to trigger chart resize - but only if container has valid dimensions
        const triggerResize = () => {
            if (chartInstanceRef.current && containerRef.current) {
                const width = containerRef.current.clientWidth;
                const height = containerRef.current.clientHeight;

                // Only resize if container has valid dimensions
                if (width <= 0 || height <= 0) {
                    return;
                }

                // Force a reflow to ensure dimensions are calculated
                containerRef.current.offsetHeight;

                // Use double requestAnimationFrame to ensure DOM has fully updated
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (chartInstanceRef.current && containerRef.current) {
                            const chart = chartInstanceRef.current as any;
                            const newWidth = containerRef.current.clientWidth;
                            const newHeight = containerRef.current.clientHeight;

                            // Only resize if dimensions are valid
                            if (newWidth <= 0 || newHeight <= 0) {
                                return;
                            }

                            // Try multiple methods to trigger resize
                            // Method 1: Try resize method if it exists
                            if (typeof chart.resize === 'function') {
                                chart.resize();
                            }
                            // Method 2: Try updateSize if it exists
                            else if (typeof chart.updateSize === 'function') {
                                chart.updateSize(newWidth, newHeight);
                            }
                            // Method 3: Try applyOptions if it exists
                            else if (typeof chart.applyOptions === 'function') {
                                chart.applyOptions({ width: newWidth, height: newHeight });
                            }
                        }
                    });
                });
            }
        };

        // Handle resize with ResizeObserver - observe wrapper to catch container size changes
        let resizeObserver: ResizeObserver | null = null;
        const elementToObserve = wrapperRef.current || containerRef.current;
        if (elementToObserve && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(entries => {
                for (const entry of entries) {
                    if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
                        triggerResize();
                    }
                }
            });
            resizeObserver.observe(elementToObserve);
        }

        // Fallback to window resize listener
        const handleResize = () => {
            triggerResize();
        };
        window.addEventListener('resize', handleResize);

        // Initial resize after a short delay to ensure chart is fully initialized
        // Multiple attempts to ensure chart renders on mobile
        // Use longer delays to ensure data is loaded first
        setTimeout(triggerResize, 300);
        setTimeout(triggerResize, 800);
        setTimeout(triggerResize, 1500);

        return () => {
            if (periodClickTimer) clearTimeout(periodClickTimer);
            container.removeEventListener('click', handlePeriodClick);
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            if (windowResizeTimeout) {
                clearTimeout(windowResizeTimeout);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', handleResize);
            container.innerHTML = '';
            chartInstanceRef.current = null;
        };
    }, [symbol, themeColorMode, isMobile, pricePrecision, activePeriod]);

    // When the symbol changes, reset to the default period so the new instrument
    // always loads with a fresh 1D view.
    const prevSymbolRef = useRef(symbol);
    useEffect(() => {
        if (prevSymbolRef.current !== symbol) {
            prevSymbolRef.current = symbol;
            setActivePeriod(DEFAULT_PERIOD);
        }
    }, [symbol]);

    // Update price axis precision when instrument config loads or changes
    useEffect(() => {
        if (!chartInstanceRef.current || !symbol) return;
        chartInstanceRef.current.setSymbol(buildSymbolInfo(symbol, pricePrecision));
    }, [symbol, pricePrecision]);

    // Listen to the shared prices WebSocket channel and push live ticks into
    // the chart via the datafeed subscribe callback, updating the latest bar's
    // close/high/low in real-time without any HTTP polling.
    useEffect(() => {
        if (!symbol) return;

        const channel = echo.channel('prices');

        const handlePriceUpdate = (payload: {
            symbol: string;
            bid: number;
            ask: number;
            last?: number;
        }) => {
            if (payload.symbol !== symbol) return;
            if (!subscribeCallbackRef.current || !latestBarRef.current) return;

            const price = payload.last ?? payload.bid;
            const updated: KLineData = {
                ...latestBarRef.current,
                close: price,
                high: Math.max(latestBarRef.current.high, price),
                low: Math.min(latestBarRef.current.low, price),
            };
            latestBarRef.current = updated;
            subscribeCallbackRef.current(updated);
        };

        channel.listen('.price.updated', handlePriceUpdate);

        return () => {
            // Remove only our listener — leaves usePriceSocket's listener intact
            channel.stopListening('.price.updated', handlePriceUpdate);
        };
    }, [symbol]);

    return (
        <div
            ref={wrapperRef}
            className="w-full h-full relative overflow-x-hidden md:overflow-x-visible"
            style={{
                minHeight: '300px',
                height: '100%',
                width: '100%',
            }}
        >
            {/* Error State */}
            {error && (
                <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-50 bg-destructive/90 text-destructive-foreground px-4 py-2 rounded-md text-sm">
                    {error}
                </div>
            )}

            {/* Chart Container */}
            <div
                ref={containerRef}
                data-chart-container="true"
                className="w-full h-full overflow-x-hidden md:overflow-x-visible"
                style={{
                    minHeight: '300px',
                    height: '100%',
                    width: '100%',
                    position: 'relative',
                }}
            />
        </div>
    );
};

export default KLineProChart;
